// components/Identicon.tsx
'use client';

import { useMemo } from 'react';

interface IdenticonProps {
  address: string;
  size?: number;
  className?: string;
}

// Generate deterministic colors from address
function generateColors(address: string): string[] {
  const seed = parseInt(address.slice(2, 10), 16);
  const colors = [];
  
  for (let i = 0; i < 3; i++) {
    const hue = (seed * (i + 1) * 137.5) % 360;
    const saturation = 45 + ((seed * (i + 1)) % 30);
    const lightness = 45 + ((seed * (i + 2)) % 25);
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }
  
  return colors;
}

// Generate pattern from address
function generatePattern(address: string): boolean[][] {
  const seed = address.toLowerCase();
  const pattern: boolean[][] = [];
  
  // Create 5x5 grid (symmetric)
  for (let y = 0; y < 5; y++) {
    pattern[y] = [];
    for (let x = 0; x < 3; x++) {
      const index = y * 3 + x;
      const charCode = seed.charCodeAt(index + 2) || 0;
      pattern[y][x] = charCode % 2 === 0;
    }
    // Mirror for symmetry
    pattern[y][3] = pattern[y][1];
    pattern[y][4] = pattern[y][0];
  }
  
  return pattern;
}

export default function Identicon({ address, size = 40, className = '' }: IdenticonProps) {
  const { pattern, colors } = useMemo(() => {
    if (!address || !address.startsWith('0x')) {
      return { pattern: [], colors: ['#e0e0e0', '#f0f0f0'] };
    }
    
    return {
      pattern: generatePattern(address),
      colors: generateColors(address)
    };
  }, [address]);

  if (!pattern.length) {
    return <div className={`rounded-full bg-gray-300 ${className}`} style={{ width: size, height: size }} />;
  }

  const cellSize = size / 5;

  return (
    <div 
      className={`rounded-full overflow-hidden ${className}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background */}
        <rect width={size} height={size} fill={colors[0]} opacity="0.3" />
        
        {/* Pattern */}
        {pattern.map((row, y) =>
          row.map((filled, x) => {
            if (!filled) return null;
            
            const colorIndex = (x + y) % colors.length;
            
            return (
              <rect
                key={`${x}-${y}`}
                x={x * cellSize}
                y={y * cellSize}
                width={cellSize}
                height={cellSize}
                fill={colors[colorIndex]}
              />
            );
          })
        )}
      </svg>
    </div>
  );
}